import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {withRouter, resolveRoute} from '@computerrock/formation-router';
import {ReactSVG} from 'react-svg';
import './ExcellImport.scss';
import {LANGUAGES} from '../../utils/ftemConstants';
import * as adminActionTypes from '../adminActionTypes';
import AdminImportTable from './AdminImportTable';
import routePaths from '../../routePaths';

const AdminSideNav = ({handleLogout}) => {
    return (
        <div className="sski-admin__sidenav pr-0">
            <ReactSVG className="sski-admin__sidenav-top-icon" src={`${process.env.PUBLIC_URL}/assets/icons/admin.svg`} alt="FTEM icon" />
            <div className="sski-admin__sidenav-list-option">
                <div className="sski-admin__sidenav-option is-flex is-justify-content-left is-align-items-center p-4 pr-0">
                    <span className="icon mr-3">
                        <ReactSVG src={`${process.env.PUBLIC_URL}/assets/icons/import.svg`} alt="Import icon" />
                    </span>
                    Import
                </div>
            </div>
            <div className="sski-admin__sidenav-list-option">
                <div
                    className="sski-admin__sidenav-logout is-flex is-justify-content-left is-align-items-center p-4 pr-0"
                    onClick={() => handleLogout()}
                >
                    <span className="icon mr-3">
                        <img src={`${process.env.PUBLIC_URL}/assets/icons/logout.png`} alt="Logout icon" />
                    </span>
                    <span>Logout</span>
                </div>
            </div>
        </div>
    );
};

const ImportDetails = ({
    sports,
    adminSports,
    language,
    file,
    setSport,
    setLanguage,
    data,
    uploadingDataLoader,
    importData,
    onReadFile,
    selectedSport,
}) => {
    // Derive filtered sports
    const filteredSports = adminSports
        ? sports.filter(sport => adminSports.some(adminSport => adminSport.itemKey === sport.id))
        : [];

    // Get unique locales for the selected sport (assuming adminSports has locale data)
    const selectedSportLocales = selectedSport
        ? adminSports
            .filter(adminSport => adminSport.itemKey === selectedSport)
            .map(adminSport => adminSport.locale || null)
            .filter(locale => locale)
        : [];
    const availableLanguages = selectedSportLocales.length
        ? LANGUAGES.filter(lang => selectedSportLocales.includes(lang.value))
        : LANGUAGES; // Fallback to all languages if none specified

    // Ensure the selected language is valid for the current sport
    useEffect(() => {
        if (adminSports && sports.length > 0 && selectedSport && !availableLanguages.some(lang => lang.value === language)) {
            setLanguage(availableLanguages[0]?.value || 'de'); // Default to first available language or 'de'
        }
    }, [selectedSport, availableLanguages, setLanguage, language, adminSports, sports]);

    // Delay rendering until all data is ready
    if (!adminSports || !sports.length || !selectedSport || !filteredSports.length) {
        return null;
    }

    return (
        <div className="sski-admin__import-container container mx-auto p-6 max-w-4xl">
            <div className="space-y-4">
                <div className="sski-admin__import-container-select-container is-flex">
                    <label htmlFor="sport" className="is-flex">
                        Sport
                    </label>
                    <div className="select">
                        <select
                            id="sport"
                            value={selectedSport}
                            onChange={e => {
                                setSport(e.target.value);
                            }}
                            className="is-fullwidth w-full rounded-md is-size-4 p-2"
                        >
                            {filteredSports.map(sport => (
                                <option value={sport.id} key={sport.idNum}>
                                    {sport.title}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className="sski-admin__import-container-select-container">
                    <label htmlFor="language" className="is-flex">
                        Language
                    </label>
                    <div className="select">
                        <select
                            id="language"
                            value={language}
                            onChange={e => setLanguage(e.target.value)}
                            className="block w-full rounded-md is-size-4 p-2"
                        >
                            {availableLanguages.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className="sski-admin__import-container-select-container is-flex is-align-items-start">
                    <label className="is-flex">Excel-Datei</label>
                    <div className="is-flex is-justify-content-center is-align-items-center">
                        <div className="file-info-box is-size-4 is-flex mr-5">{file ? file?.name : ''}</div>
                        <button className="sski-admin__import-file-btn button">
                            <input
                                id="file"
                                type="file"
                                accept=".xlsx"
                                onChange={onReadFile}
                            />
                            Choose file
                        </button>
                    </div>
                </div>
                <div className="flex justify-end mt-6">
                    <button
                        type="submit"
                        className={`sski-admin__import-btn button is-rounded is-size-4 ${
                            uploadingDataLoader ? 'is-loading' : ''
                        }`}
                        disabled={!data || !filteredSports.length}
                        onClick={importData}
                    >
                        Import
                    </button>
                </div>
            </div>
        </div>
    );
};

const ExcelImport = ({sports, adminSports, importExcell, excel, isLoading,
    importedData, errors, handlePublishData, handleLogout,
    errorMsg, jwtToken, updatedData, history}) => {
    const [selectedSport, setSport] = useState('');
    const [language, setLanguage] = useState('de');
    const [file, setFile] = useState(null);
    const [data, setData] = useState('');
    const [errorData, setErrorData] = useState([]);
    const [uploadingDataLoader, setUploadingDataLoader] = useState(isLoading);
    const [publishingLoader, setPublishingLoader] = useState(false);
    const [publishedData, setPublishedData] = useState([]);

    const onReadFile = e => {
        const file = e.target.files[0];
        readFile(file);
        setFile(file);
    };

    const readSheet = workbook => {
        const titles = [];
        const result = {topic: [], subTopic: [], elements: [], links: []};
        const createTextObject = ({
            text,
            isBold,
            isItalic,
            isUnderline,
            textTemplate,
            linkItemKey,
            id,
        }) => {
            const textStyle = {
                ...(isBold && {bold: true}),
                ...(isItalic && {italic: true}),
                ...(isUnderline && {underline: true}),
            };

            const createTextElement = textContent => ({
                type: 'text',
                ...textStyle,
                text: textContent,
            });

            const hasTemplate = textTemplate?.length > 0;
            const target = hasTemplate ? textTemplate[0].children : [];
            const links = isMultiLink(text);
            const isMultiLinks = links.length > 1;
            const isTextWithLink = links.length > 0;

            if (!isTextWithLink) {
                const textElement = createTextElement(text);
                if (hasTemplate) {
                    target.push(textElement);
                    return textTemplate;
                }
                textTemplate.push({type: 'paragraph', children: [textElement]});
                return textTemplate;
            }

            let elements = [];
            let remainingText = text;

            if (isMultiLinks) {
                links.forEach(linkContent => {
                    const [linkText, url] = linkContent.split('|');
                    const linkMatch = `[[${linkContent}]]`;
                    const [before, after] = remainingText.split(linkMatch, 2);

                    if (before) {
                        elements.push(createTextElement(before));
                    }

                    if (!url) {
                        linkItemKey.push(linkText);
                    } else {
                        elements.push({
                            type: 'link',
                            url,
                            children: [createTextElement(linkText)],
                        });
                    }

                    remainingText = after ?? '';
                });

                if (remainingText) {
                    elements.push(createTextElement(remainingText));
                }
            } else {
                const [linkText, url] = links[0].split('|');
                const splitText = text.split(`[[${links[0]}]]`);
                const textLeft = splitText[0];
                const textRight = splitText[1] ?? '';

                if (!url) {
                    linkItemKey.push(linkText);
                    elements = [createTextElement(textLeft)];
                } else {
                    elements = [
                        createTextElement(textLeft),
                        {
                            type: 'link',
                            url,
                            children: [createTextElement(linkText)],
                        },
                        createTextElement(textRight),
                    ];
                }
            }

            if (hasTemplate) {
                target.push(...elements);
                return textTemplate;
            }

            textTemplate.push({type: 'paragraph', children: elements});
            return textTemplate;
        };

        const isMultiLink = text => {
            const regex = /\[\[(.*?)\]\]/g;
            const matches = [];
            let match;
            while ((match = regex.exec(text)) !== null) {
                matches.push(match[1]);
            }
            return matches;
        };

        const formatRFTObjectToStrapi = (text, textTemplate, linkItemKey, id) => {
            const getText = text.text;
            const isBold = text.font?.bold;
            const isItalic = text.font?.italic;
            const isUnderline = text.font?.underline;
            createTextObject({text: getText, isBold, textTemplate, isItalic, isUnderline, linkItemKey, id});
        };

        const getTextFromRFT = textObject => {
            return textObject.richText.reduce((accumulator, current) => {
                return accumulator + current.text;
            }, '');
        };

        const getMergedCell = (sheet, cell) => {
            let result = sheet.getCell(cell).value;
            if (result?.formula) {
                result = sheet.getCell(result.formula).value;
            }
            return result;
        };

        workbook.eachSheet((sheet, id) => {
            const levelName = id === 1 ? 'homepage' : 'detail';
            sheet.eachRow((row, rowNumber) => {
                if (rowNumber === 1) {
                    titles.push(row.values);
                } else {
                    if (id === 3) {
                        return result.links.push({
                            itemKey: row.values[1],
                            title: row.values[2],
                            link: row.values[3]?.hyperlink ?? row.values[3]?.text,
                        });
                    }
                    if (!result.topic.find(topicName => topicName.itemKey === row.values[1])) {
                        result.topic.push({
                            name: row.values[2],
                            itemKey: row.values[1],
                            topicGroup: row.values[3],
                            level: levelName,
                            sortKey: row.values[6] ?? '',
                        });
                    }
                    result.subTopic.push({
                        itemKey: row.values[4],
                        name: row.values[5],
                        sortKey: row.values[6] ?? '',
                        topic: '',
                        level: levelName,
                    });

                    row.values.map((rowValue, index) => {
                        if (index > 6) {
                            const getSubPhaseName = titles[id - 1][index];
                            const subPhaseName = getSubPhaseName.toLowerCase();
                            if (subPhaseName.includes('link-key')) {
                                return;
                            }
                            const elementKey = row.values[4] + '-' + subPhaseName;
                            const titleText = row.values[5];
                            const title = `${titleText?.richText ? getTextFromRFT(titleText) : titleText} ${getSubPhaseName}`;
                            const textTemplate = [];
                            const linkItemKey = [];
                            const createText = rowValue?.richText?.map(text => {
                                formatRFTObjectToStrapi(text, textTemplate, linkItemKey, elementKey);
                            });

                            if (textTemplate.length === 0) {
                                if (rowValue?.sharedFormula) {
                                    console.log(rowValue, 'rowValue Shared', elementKey, 'Element Key');
                                    createTextObject({text: '', textTemplate, linkItemKey, id: elementKey});
                                } else if (rowValue?.formula) {
                                    const originalText = getMergedCell(sheet, rowValue?.formula);
                                    if (originalText?.richText) {
                                        originalText.richText.map(text => {
                                            formatRFTObjectToStrapi(text, textTemplate, linkItemKey, elementKey);
                                        });
                                    } else {
                                        const getFormattedText = getMergedCell(sheet, rowValue?.formula || rowValue?.sharedFormula);
                                        createTextObject({text: getFormattedText, textTemplate, linkItemKey, id: elementKey});
                                    }
                                } else {
                                    createTextObject({text: rowValue, textTemplate, linkItemKey, id: elementKey});
                                }
                            }

                            if (textTemplate.length === 0) {
                                const originalText = getMergedCell(sheet, rowValue?.formula || rowValue?.sharedFormula);
                                console.log(originalText, 'originalText', subPhaseName + ' ' + row.values[4], index, 'index', rowValue, 'ROW VALUE');
                            }

                            if (!elementKey || !row.values[4]) {
                                return;
                            }
                            result.elements.push({
                                itemKey: elementKey + '-' + selectedSport,
                                text: textTemplate,
                                ftemPhase: subPhaseName,
                                subTopic: row.values[4],
                                level: levelName,
                                title: title,
                                links: linkItemKey[0],
                                sport: '',
                            });
                        }
                    });
                }
            });
        });
        return result;
    };

    const logoutFn = () => {
        history.replace(resolveRoute(routePaths.DASHBOARD, {}));
        localStorage.removeItem('jwt');
        handleLogout({});
    };

    const readFile = file => {
        if (file.type === 'text/csv' || /\.xlsx$/i.test(file.name)) {
            // setDataFormat(dataFormats.CSV);
        } else {
            throw new Error(`File type ${file.type} not supported.`);
        }

        const wb = new excel.Workbook();
        const reader = new FileReader();

        reader.readAsArrayBuffer(file);
        reader.onload = () => {
            const buffer = reader.result;
            wb.xlsx.load(buffer).then(workbook => {
                const prepareData = readSheet(workbook);
                setData(prepareData);
                console.log(prepareData, 'DATA 22');
            });
        };
    };

    const importData = () => {
        setUploadingDataLoader(true);
        const elementsData = data.elements;
        const linksData = data.links;
        importExcell({
            lang: language,
            elementsData,
            linksData,
            sport: selectedSport,
        });
    };

    const publishData = () => {
        setPublishingLoader(true);
        const elementsData = importedData;
        handlePublishData({
            lang: language,
            elementsData,
            sport: selectedSport,
            jwtToken: jwtToken,
        });
    };

    // Set initial sport when sports data is available
    useEffect(() => {
        if (sports?.length > 0 && adminSports?.length > 0 && !selectedSport) {
            const filteredSports = sports.filter(sport => adminSports.some(adminSport => adminSport.itemKey === sport.id));
            if (filteredSports.length > 0) {
                setSport(filteredSports[0].id);
            }
        }
    }, [sports, adminSports, selectedSport, setSport]);

    useEffect(() => {
        setPublishedData([]);
    }, [language, sports]);

    return (
        <div className="sski-admin-plugin is-flex">
            <AdminSideNav handleLogout={logoutFn} />
            <div className="sski-admin-plugin__content">
                <h2 className="sski-admin-plugin_content-title is-size-3 has-text-weight-bold">Import</h2>
                <div className="sski-admin-plugin_content-subtitle mb-2">Die Daten für die FTEM-Übersicht und den Athlet:innen-Weg können einfach in Form einer Excel-Tabelle erfasst und hier importiert werden.</div>
                <div className="sski-admin-plugin_link-icon">
                    <a className="has-text-link is-flex is-justify-content-left is-align-items-center" href="#">
                        <img
                            alt="Link icon"
                            src={`${process.env.PUBLIC_URL}/assets/icons/excelImport.png`}
                            className="mr-2"
                        />
                        Excel-Vorlage herunterladen
                    </a>
                </div>
                {sports.length > 0 && adminSports?.length > 0 && (
                    <ImportDetails
                        sports={sports}
                        adminSports={adminSports}
                        language={language}
                        setLanguage={setLanguage}
                        file={file}
                        setFile={setFile}
                        importData={importData}
                        data={data}
                        onReadFile={onReadFile}
                        uploadingDataLoader={isLoading}
                        selectedSport={selectedSport}
                        setSport={setSport}
                    />
                )}
                {importedData.length > 0 && (
                    <AdminImportTable
                        importedData={importedData}
                        errors={errors}
                        publishData={publishData}
                        errorMsg={errorMsg}
                    />
                )}
                {updatedData?.length > 0 && (
                    <AdminImportTable
                        importedData={updatedData}
                        publishData={publishData}
                        isPublish={true}
                        errors={updatedData.errors}
                    />
                )}
            </div>
        </div>
    );
};

const mapStateToProps = state => {
    return {
        categoryOptions: state.categories.categoryOptions,
        sports: state.dashboard.sports,
        isLoading: state.admin.isLoading,
        importedData: state.admin.importedData,
        updatedData: state.admin.updatedData,
        errors: state.admin.errors,
        errorMsg: state.admin.errorMsg,
        jwtToken: state.admin.jwtToken,
        adminSports: state.admin.adminSports,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        importExcell: payload => dispatch({
            type: adminActionTypes.IMPORT_DATA_REQUEST,
            payload,
        }),
        handlePublishData: payload => dispatch({
            type: adminActionTypes.PUBLISH_DATA_REQUEST,
            payload,
        }),
        handleLogout: payload => dispatch({
            type: adminActionTypes.LOGOUT,
        }),
    };
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ExcelImport));
